import { Controller } from "@hotwired/stimulus"

// Shows a spinner overlay during Turbo navigations, then hides it when the response finishes rendering.
export default class extends Controller {
  static targets = ["overlay", "content"]
  static values = { showOnTurboVisit: Boolean }

  connect() {
    this.heldForFullReload = false
    this.pageVisitInProgress = false
    // Full-page visits remove this controller with the body swap. Do not hide on
    // turbo:before-cache / turbo:before-render — that drops the overlay while the
    // old page is still visible. Clear any stuck overlay from a cached snapshot.
    this.hide()

    this.boundHide = this.hide.bind(this)
    this.boundOnVisit = this.onVisit.bind(this)
    this.boundHoldForFullReload = this.holdForFullReload.bind(this)
    this.boundOnPageShow = this.onPageShow.bind(this)
    // Frame events target <turbo-frame> and do not bubble to document; capture phase
    // runs from document downward so we still see turbo:frame-render / turbo:frame-load.
    this.frameCaptureOptions = { capture: true }
    document.addEventListener("turbo:frame-render", this.boundHide, this.frameCaptureOptions)
    document.addEventListener("turbo:frame-load", this.boundHide, this.frameCaptureOptions)
    document.addEventListener("turbo:submit-end", this.boundHide, this.frameCaptureOptions)
    document.addEventListener("turbo:before-stream-render", this.boundHide)
    document.addEventListener("turbo:fetch-request-error", this.boundHide)
    document.addEventListener("turbo:render", this.boundHide)
    document.addEventListener("turbo:load", this.boundHide)
    document.addEventListener("turbo:reload", this.boundHoldForFullReload)
    window.addEventListener("pageshow", this.boundOnPageShow)

    if (this.showOnTurboVisitValue) {
      document.addEventListener("turbo:visit", this.boundOnVisit)
    }
  }

  disconnect() {
    document.removeEventListener("turbo:frame-render", this.boundHide, this.frameCaptureOptions)
    document.removeEventListener("turbo:frame-load", this.boundHide, this.frameCaptureOptions)
    document.removeEventListener("turbo:submit-end", this.boundHide, this.frameCaptureOptions)
    document.removeEventListener("turbo:before-stream-render", this.boundHide)
    document.removeEventListener("turbo:fetch-request-error", this.boundHide)
    document.removeEventListener("turbo:render", this.boundHide)
    document.removeEventListener("turbo:load", this.boundHide)
    document.removeEventListener("turbo:reload", this.boundHoldForFullReload)
    document.removeEventListener("turbo:visit", this.boundOnVisit)
    window.removeEventListener("pageshow", this.boundOnPageShow)
  }

  onPageShow() {
    this.heldForFullReload = false
    this.hide()
  }

  onVisit() {
    this.pageVisitInProgress = true
    this.show()
  }

  // Tracked head tags differ across layouts (e.g. public home → admin). Turbo XHRs
  // the next page, then full-reloads. It still fires turbo:load on the old document
  // before that reload paints, which would drop the overlay while the new page loads.
  holdForFullReload() {
    this.heldForFullReload = true
    this.show()
  }

  show() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("hidden")
      this.overlayTarget.classList.add("flex")
    }
    if (this.hasContentTarget) {
      this.contentTarget.classList.add("invisible")
    }
  }

  hide(event) {
    if (this.heldForFullReload) return
    if (this.shouldKeepVisibleAfterSubmit(event)) return
    if (this.shouldKeepVisibleDuringPageVisit(event)) return

    this.pageVisitInProgress = false

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("hidden")
      this.overlayTarget.classList.remove("flex")
    }
    if (this.hasContentTarget) {
      this.contentTarget.classList.remove("invisible")
    }
  }

  shouldKeepVisibleAfterSubmit(event) {
    return event?.type === "turbo:submit-end" &&
      event.detail?.success &&
      event.target?.dataset.spinnerOverlayKeepVisibleOnSuccess === "true"
  }

  shouldKeepVisibleDuringPageVisit(event) {
    if (!this.pageVisitInProgress) return false

    const type = event?.type
    return type === "turbo:frame-render" ||
      type === "turbo:frame-load" ||
      type === "turbo:before-stream-render"
  }
}
